"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSettlementsForTeamManagerQuery = exports.getSettlementsForTeamQuery = exports.getSettlementsForCompanyQuery = exports.getManagerMetricsQuery = exports.getTeamMetricsQuery = exports.getCompanyMetricsQuery = void 0;
const react_query_1 = require("@tanstack/react-query");
const pino_1 = __importDefault(require("pino"));
const dashboardRepository_1 = require("../repositories/dashboardRepository");
const teamRepository_1 = require("../repositories/teamRepository");
const Root_1 = require("../router/Root");
const paymentQuery_1 = require("./paymentQuery");
const userQuery_1 = require("./userQuery");
const logger = (0, pino_1.default)();
const dashboardKeys = {
    getCompanyMetrics: () => ['getCompanyMetrics'],
    getTeamMetrics: (teamId) => ['getTeamMetrics', teamId, 'teamId'],
    getManagerMetrics: (teamId) => ['getManagerMetrics', teamId, 'teamId'],
    getSettlementsForCompany: (fromDate, toDate) => ['getSettlementsForCompany', fromDate, 'fromDate', toDate, 'toDate'],
    getSettlementsForTeam: (teamId, fromDate, toDate) => ['getSettlementsForCompany', teamId, 'teamId', fromDate, 'fromDate', toDate, 'toDate'],
    getSettlementsForTeamManager: (teamId, fromDate, toDate) => ['getSettlementsForCompany', teamId, 'teamId', fromDate, 'fromDate', toDate, 'toDate'],
};
const changeOverviewData = async (item) => {
    try {
        const totalApprovedAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(item.currency, item.approvedAmount));
        const totalAvailableBalance = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(item.currency, item.availableBalance));
        const totalSpentAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(item.currency, item.spentAmount));
        item.totalApprovedAmount = totalApprovedAmount || '';
        item.totalAvailableBalance = totalAvailableBalance || '';
        item.totalSpentAmount = totalSpentAmount || '';
        return item;
    }
    catch (error) {
        logger.error('Error processing overview data:', error);
        throw error;
    }
};
const changeTeamData = async (team) => {
    try {
        const totalApprovedAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(team.metrics[0].currency, team.metrics[0].approvedAmount.toString()));
        const totalAvailableBalance = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(team.metrics[0].currency, team.metrics[0].availableBalance.toString()));
        const totalSpentAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(team.metrics[0].currency, team.metrics[0].spentAmount.toString()));
        team.totalApprovedAmount = totalApprovedAmount || '';
        team.totalAvailableBalance = totalAvailableBalance || '';
        team.totalSpentAmount = totalSpentAmount || '';
        team.activeCardCount = team.metrics[0].activeCardCount;
        return team;
    }
    catch (error) {
        logger.error('Error processing team data:', error);
        throw error;
    }
};
const changePeopleData = async (people) => {
    try {
        const totalApprovedAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(people.metrics[0].currency, people.metrics[0].approvedAmount));
        const totalAvailableBalance = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(people.metrics[0].currency, people.metrics[0].availableBalance));
        const totalSpentAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(people.metrics[0].currency, people.metrics[0].spentAmount));
        const member = await Root_1.queryClient.ensureQueryData((0, userQuery_1.userByIdQuery)(people.memberId));
        people.totalApprovedAmount = totalApprovedAmount || '';
        people.totalAvailableBalance = totalAvailableBalance || '';
        people.totalSpentAmount = totalSpentAmount || '';
        people.memberName = member.fullName || '';
        people.activeCardCount = people.metrics[0].activeCardCount;
        return people;
    }
    catch (error) {
        logger.error('Error processing people data:', error);
        throw error;
    }
};
const getCompanyMetricsQuery = () => ({
    queryKey: dashboardKeys.getCompanyMetrics(),
    queryFn: async () => {
        const initialMetrics = await dashboardRepository_1.DashboardRepository.getCompanyMetrics();
        const overviewPromises = initialMetrics.overview.map(changeOverviewData);
        const teamsData = await teamRepository_1.TeamRepository.getTeamsPage({ state: 'active' });
        teamsData?.forEach(team => {
            team.metrics = [
                {
                    approvedAmount: 0,
                    spentAmount: 0,
                    availableBalance: 0,
                    currency: '',
                    activeCardCount: 0,
                },
            ];
            initialMetrics.teams.forEach(t => {
                if (t.teamId === team.id && t.metrics.length > 0) {
                    team.metrics = t.metrics;
                }
            });
        });
        const teamsPromises = teamsData?.map(changeTeamData) || [];
        const [overview, teams] = await Promise.all([Promise.all(overviewPromises), Promise.all(teamsPromises)]);
        return { overview, teams };
    },
});
exports.getCompanyMetricsQuery = getCompanyMetricsQuery;
const getTeamMetricsQuery = (teamId) => ({
    queryKey: dashboardKeys.getTeamMetrics(teamId),
    queryFn: async () => {
        const initialMetrics = await dashboardRepository_1.DashboardRepository.getTeamMetrics(teamId);
        const overviewPromises = initialMetrics.overview.map(changeOverviewData);
        const peoplePromises = initialMetrics.people?.map(changePeopleData) || [];
        const [overview, people] = await Promise.all([Promise.all(overviewPromises), Promise.all(peoplePromises)]);
        return { overview, people };
    },
});
exports.getTeamMetricsQuery = getTeamMetricsQuery;
const getManagerMetricsQuery = (teamId) => ({
    queryKey: dashboardKeys.getManagerMetrics(teamId),
    queryFn: async () => {
        const initialMetrics = await dashboardRepository_1.DashboardRepository.getManagerMetrics(teamId);
        const overviewPromises = initialMetrics.overview.map(changeOverviewData);
        const peoplePromises = initialMetrics.people?.map(changePeopleData) || [];
        const [overview, people] = await Promise.all([Promise.all(overviewPromises), Promise.all(peoplePromises)]);
        return { overview, people };
    },
});
exports.getManagerMetricsQuery = getManagerMetricsQuery;
const getSettlementsForCompanyQuery = ({ fromDate, toDate }) => {
    return (0, react_query_1.queryOptions)({
        queryKey: dashboardKeys.getSettlementsForCompany(fromDate, toDate),
        queryFn: () => dashboardRepository_1.DashboardRepository.getSettlementsForCompany(fromDate, toDate),
        enabled: Boolean(fromDate && toDate),
    });
};
exports.getSettlementsForCompanyQuery = getSettlementsForCompanyQuery;
const getSettlementsForTeamQuery = ({ teamId, fromDate, toDate, }) => (0, react_query_1.queryOptions)({
    queryKey: dashboardKeys.getSettlementsForTeam(teamId, fromDate, toDate),
    queryFn: () => dashboardRepository_1.DashboardRepository.getSettlementsForTeam(teamId, fromDate, toDate),
    enabled: Boolean(teamId && fromDate && toDate),
});
exports.getSettlementsForTeamQuery = getSettlementsForTeamQuery;
const getSettlementsForTeamManagerQuery = ({ teamId, fromDate, toDate, }) => (0, react_query_1.queryOptions)({
    queryKey: dashboardKeys.getSettlementsForTeamManager(teamId, fromDate, toDate),
    queryFn: () => dashboardRepository_1.DashboardRepository.getSettlementsForTeamManager(teamId, fromDate, toDate),
    enabled: Boolean(teamId && fromDate && toDate),
});
exports.getSettlementsForTeamManagerQuery = getSettlementsForTeamManagerQuery;
